import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import { useCallback, useRef, useState } from "react";

import { logger } from "#app/monitoring/Logger";
import { showErrorToast } from "#app/notifications";
import type { FileUploadFile } from "#components/molecules/FileUpload/FileUpload";
import { useCompany } from "#modules/company/view/useCompany";
import { useCompanyModelType } from "#modules/company/view/useCompanyModelType";
import { uploadPartFiles } from "#modules/files/infra/uploadPartFiles.api";
import { createFileHash } from "#modules/parts/domain/createFileHash";
import { createPart, CreatePartRequestBody } from "#modules/parts/infra/createPart.api";
import { quoteRequestQuery } from "#modules/quoteRequests/infra/quoteRequest.query";
import { useCreateQuoteRequest } from "#modules/quoteRequests/view/useCreateQuoteRequest";
import { PredictionModelTypeSchema, resolveAssemblySplitMode } from "shared";

type UploadedFile = {
  name: string;
  files: CreatePartRequestBody["files"];
};

export const useCreateQuoteRequestFiles = (existingQuoteRequestId?: string) => {
  const [files, setFiles] = useState<FileUploadFile[]>([]);
  const [quoteRequestId, setQuoteRequestId] = useState<string | null>(
    existingQuoteRequestId ?? null,
  );
  const [isPending, setIsPending] = useState(false);

  const quoteRequestIdRef = useRef<string | null>(existingQuoteRequestId ?? null);
  const partIndexRef = useRef(0);
  const uploadCountRef = useRef(0);
  const uploadedFilesRef = useRef(new Map<string, UploadedFile>());
  const removedHashesRef = useRef(new Set<string>());

  const { createQuoteRequest } = useCreateQuoteRequest();
  const { company } = useCompany();
  const modelType = useCompanyModelType();
  const queryClient = useQueryClient();

  const updateFileProgress = useCallback((hash: string, progress: number) => {
    setFiles((prev) => prev.map((f) => (f.hash === hash ? { ...f, progress } : f)));
  }, []);

  const addFiles = useCallback(
    async (fileList: FileList) => {
      const fileEntries = await Promise.all(
        Array.from(fileList).map(async (file) => ({
          file,
          hash: await createFileHash(file),
        })),
      );

      const uniqueByHash = new Map(fileEntries.map((e) => [e.hash, e]));
      const newEntries = Array.from(uniqueByHash.values()).filter(
        ({ hash }) => !files.some((f) => f.hash === hash),
      );

      if (newEntries.length === 0) {
        return;
      }

      if (!quoteRequestIdRef.current) {
        try {
          const qr = await createQuoteRequest();
          if (!qr) {
            throw new Error("Failed to create quote request");
          }
          quoteRequestIdRef.current = qr.id;
          setQuoteRequestId(qr.id);
        } catch (err) {
          logger.error(err);
          showErrorToast({ title: t("quoteRequestsV2.modal.uploadError") });
          return;
        }
      }

      setFiles((prev) => [
        ...prev,
        ...newEntries.map(({ hash, file }) => ({ hash, name: file.name, progress: 0 })),
      ]);

      uploadCountRef.current += newEntries.length;
      setIsPending(true);

      await Promise.all(
        newEntries.map(async ({ file, hash }) => {
          try {
            const uploadedFiles = await uploadPartFiles(
              [{ data: file, name: file.name, hash }],
              (progress) => updateFileProgress(hash, progress),
            );

            if (removedHashesRef.current.has(hash)) {
              return;
            }

            uploadedFilesRef.current.set(hash, { name: file.name, files: uploadedFiles });
            updateFileProgress(hash, 100);
          } catch (err) {
            logger.error(err);
            showErrorToast({ title: t("quoteRequestsV2.modal.uploadError") });
            setFiles((prev) => prev.filter((f) => f.hash !== hash));
          } finally {
            uploadCountRef.current--;
            if (uploadCountRef.current === 0) {
              setIsPending(false);
            }
          }
        }),
      );
    },
    [files, createQuoteRequest, updateFileProgress],
  );

  const commitFiles = useCallback(async () => {
    const currentQuoteRequestId = quoteRequestIdRef.current;
    const uploadedFiles = Array.from(uploadedFilesRef.current.entries());
    uploadedFilesRef.current.clear();

    if (!currentQuoteRequestId || uploadedFiles.length === 0) {
      return;
    }

    await Promise.all(
      uploadedFiles.map(async ([hash, uploadedFile]) => {
        try {
          const index = partIndexRef.current++;

          await createPart({
            name: uploadedFile.name.split(".")[0] ?? uploadedFile.name,
            files: uploadedFile.files,
            quoteRequestId: currentQuoteRequestId,
            index,
            assemblySplitMode: resolveAssemblySplitMode(company?.companySettings.assemblySplitMode),
            computeEmbeddings: false,
            computeGraphs: modelType !== PredictionModelTypeSchema.enum.CLUSTERING,
            shouldCreateQuoteRequestLine: true,
          });
        } catch (err) {
          logger.error(err);
          showErrorToast({ title: t("quoteRequestsV2.modal.uploadError") });
          setFiles((prev) => prev.filter((f) => f.hash !== hash));
        }
      }),
    );

    await queryClient.invalidateQueries(quoteRequestQuery(currentQuoteRequestId));
  }, [company, modelType, queryClient]);

  const removeFile = useCallback((hash: string) => {
    removedHashesRef.current.add(hash);
    uploadedFilesRef.current.delete(hash);
    setFiles((prev) => prev.filter((f) => f.hash !== hash));
  }, []);

  const reset = useCallback(() => {
    setFiles([]);
    setQuoteRequestId(existingQuoteRequestId ?? null);
    setIsPending(false);
    quoteRequestIdRef.current = existingQuoteRequestId ?? null;
    partIndexRef.current = 0;
    uploadCountRef.current = 0;
    uploadedFilesRef.current.clear();
    removedHashesRef.current.clear();
  }, [existingQuoteRequestId]);

  return { files, addFiles, removeFile, quoteRequestId, isPending, reset, commitFiles };
};
